// Account badge catalog. Single source of truth for every badge
// rendered next to a username — what it looks like, what its
// tooltip says, who gets to issue it.
//
// Design rules:
//
//   1. The verified check (`link-staff`) is reserved for actual
//      Link Staff and is granted only by the admin tooling. There
//      is no opt-in path for users.
//   2. OG-ness is the default for every account (claim-once
//      `@name`) and therefore gets no badge — the `@name` itself
//      is the proof. Adding a badge for every user would dilute
//      the meaning of the others.
//   3. Every other badge is awarded by an event the system can
//      verify (joined during launch, paid for Link Plus, etc.).
//
// To add a new badge: define it here, add the placeholder
// renderer in `apps/web/src/components/BadgeRow.tsx`, then award
// it by adding the id to the user's `badges` array.

import type { ReactNode } from "react";
import type { AccountBadgeId } from "@link/shared/api/constants";
import {
  BadgeCheck,
  Crown,
  Gift,
  Heart,
  Sparkles,
  Star,
  Trophy,
} from "lucide-react";
import { createElement } from "react";

export type BadgeId = AccountBadgeId;

interface BadgeMeta {
  id: BadgeId;
  /** Short label used as `aria-label` and tooltip first line. */
  label: string;
  /** Optional second line of tooltip context. */
  tooltip: string;
  /** Renderer for the icon at the row's chosen size. */
  icon: (sizeClass: string) => ReactNode;
  /** Tailwind class for the badge color (icon-fill). */
  toneClass: string;
  /** Canonical artwork color that must not be remapped by a theme. */
  toneColor: string;
}

export const BADGES: Record<BadgeId, BadgeMeta> = {
  "link-staff": {
    id: "link-staff",
    label: "Link Staff",
    tooltip: "Verified employee of Link.",
    icon: (sz) =>
      createElement(BadgeCheck, {
        className: sz,
        fill: "currentColor",
        stroke: "white",
        strokeWidth: 2.5,
      }),
    // Cool sky blue keeps the "verified" affordance distinct from
    // the strawberry app accent so users can tell at a glance.
    toneClass: "text-sky-500",
    toneColor: "hsl(199 89% 48%)",
  },
  "early-user": {
    id: "early-user",
    label: "Early User",
    tooltip: "Joined during launch.",
    icon: (sz) =>
      createElement(Sparkles, {
        className: sz,
        fill: "currentColor",
        stroke: "white",
        strokeWidth: 1.5,
      }),
    toneClass: "text-strawberry-500",
    toneColor: "hsl(343 70% 65%)",
  },
  "link-plus": {
    id: "link-plus",
    label: "Link Plus",
    tooltip: "Active Link Plus subscriber.",
    icon: (sz) =>
      createElement(Gift, {
        className: sz,
        fill: "currentColor",
        stroke: "white",
        strokeWidth: 1.5,
      }),
    toneClass: "text-grape-500",
    toneColor: "hsl(265 45% 58%)",
  },
  "og-supporter": {
    id: "og-supporter",
    label: "OG Supporter",
    tooltip: "Subscribed to Link Plus during the first month.",
    icon: (sz) =>
      createElement(Heart, {
        className: sz,
        fill: "currentColor",
        stroke: "white",
        strokeWidth: 1.5,
      }),
    toneClass: "text-rose-500",
    toneColor: "hsl(350 89% 60%)",
  },
  founder: {
    id: "founder",
    label: "Founder",
    tooltip: "Founder of Link.",
    icon: (sz) =>
      createElement(Crown, {
        className: sz,
        fill: "currentColor",
        stroke: "white",
        strokeWidth: 1.5,
      }),
    toneClass: "text-amber-500",
    toneColor: "hsl(38 92% 50%)",
  },
  "bug-hunter": {
    id: "bug-hunter",
    label: "Bug Hunter",
    tooltip: "Reported a verified security issue.",
    icon: (sz) =>
      createElement(Trophy, {
        className: sz,
        fill: "currentColor",
        stroke: "white",
        strokeWidth: 1.5,
      }),
    toneClass: "text-emerald-500",
    toneColor: "hsl(160 84% 39%)",
  },
};

/**
 * Suggested ordering when a user has multiple badges. Earlier
 * entries render first (most prestigious / identity-bearing on
 * the left).
 */
const BADGE_ORDER: ReadonlyArray<BadgeId> = [
  "link-staff",
  "founder",
  "bug-hunter",
  "early-user",
  "og-supporter",
  "link-plus",
];

/**
 * Sort + dedupe a badge list so callers don't have to think about
 * order. Keeps the canonical "staff first, perks last" reading.
 */
export function sortBadges(badges: ReadonlyArray<BadgeId>): BadgeId[] {
  const set = new Set(badges);
  return BADGE_ORDER.filter((id) => set.has(id));
}

/** Narrow untrusted API badge strings to ids the current client can render. */
export function knownBadges(badges: ReadonlyArray<string>): BadgeId[] {
  return badges.filter((id): id is BadgeId => id in BADGES);
}

/** Compact identity surfaces expose only the staff verification mark inline. */
export function verifiedBadgeOnly(badges: ReadonlyArray<BadgeId>): BadgeId[] {
  return badges.includes("link-staff") ? ["link-staff"] : [];
}

/**
 * Fallback for any leftover Star usage so the import survives a
 * typo. Not exported from the catalog — kept here so unused-icon
 * warnings don't fire.
 */
void Star;
